import { useMemo, useState, type ReactNode } from "react";
import { useIntl } from "react-intl";

import LoadingIndicator from "common/core/loading_indicator";
import { useQuery } from "util/graphql";
import { type ChildRenderProps } from "common/transactions/details";
import { useA11y } from "common/accessibility";
import { useDocumentTitles } from "util/document_title";
import { Container, Row as ResponsiveRow, Column } from "common/core/responsive";
import { useProofDefend, useProofDefendUpsell } from "util/feature_detection";

// Components reused from V1
import { CustomerSwitcher } from "../customer_switcher";
import { RiskModal, useRiskModalState } from "../modals/risk";
import RiskAlertMessage from "../risk_alert_message";
// Components new for V2
import { IdentityTabV2AnalyticsProvider } from "./analytics";
import { Customer } from "./customer";
import { UpsellBanner } from "./components/upsell_banner";
import IdentityDetailsQuery, {
  type IdentityDetails_documentBundle_DocumentBundle as DocumentBundle,
} from "./index.query.graphql";
import Styles from "./index.module.scss";

type TransactionDetailsBundle = Pick<ChildRenderProps["bundle"], "id">;
type TransactionDetailsOrganization = Pick<
  ChildRenderProps["organization"],
  "id" | "proofDefend" | "defendRiskScoresTrial" | "showIdentityInformation"
>;

type InnerProps = {
  bundle: DocumentBundle;
  organization: TransactionDetailsOrganization;
};

type OuterProps = {
  bundle: TransactionDetailsBundle;
  organization: TransactionDetailsOrganization;
};

function Row({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <ResponsiveRow gutterWidth={16} className={className}>
      {children}
    </ResponsiveRow>
  );
}

function IdentityDetails({ bundle, organization }: InnerProps) {
  const customerSigners = bundle.organizationTransaction.customerSigners;

  const proofDefend = useProofDefend(organization);
  const proofDefendUpsell = useProofDefendUpsell(organization);
  const [filteredCustomerId, setFilteredCustomerId] = useState<string>(customerSigners[0].id);
  const riskModalState = useRiskModalState();

  const customerSigner = useMemo(
    () => customerSigners.find((cs) => cs.id === filteredCustomerId),
    [customerSigners, filteredCustomerId],
  );

  if (!customerSigner) {
    throw new Error(`Missing customer signer with ID: ${filteredCustomerId}`);
  }

  return (
    <>
      {riskModalState.status === "open" && (
        <RiskModal
          customerSigners={customerSigners}
          customerSignerId={riskModalState.customerSignerId}
          signerIdentityId={riskModalState.signerIdentityId}
          onChangeAttempt={riskModalState.changeAttempt}
          onClose={riskModalState.closeModal}
        />
      )}
      <Container fluid noSpacing className={Styles.container}>
        <RiskAlertMessage
          customerSigners={customerSigners}
          proofDefend={proofDefend}
          openRiskModal={riskModalState.openModal}
        />
        {proofDefendUpsell && (
          <Row>
            <Column>
              <UpsellBanner />
            </Column>
          </Row>
        )}
        {customerSigners.length > 1 && (
          <Row>
            <Column>
              <CustomerSwitcher
                customerSigners={customerSigners}
                customerId={filteredCustomerId}
                onChangeCustomer={setFilteredCustomerId}
                showRiskLevel={proofDefend}
                showAllOption={false}
              />
            </Column>
          </Row>
        )}
        <Row>
          <Column className={Styles.contentContainer}>
            <Customer
              customerSigner={customerSigner}
              documentBundle={bundle}
              proofDefend={proofDefend}
            />
          </Column>
        </Row>
      </Container>
    </>
  );
}

export function IdentityDetailsV2({ bundle, organization }: OuterProps) {
  const { data, loading } = useQuery(IdentityDetailsQuery, {
    variables: { documentBundleId: bundle.id },
  });
  const intl = useIntl();
  useA11y().useDocumentEntitler({
    title: intl.formatMessage(useDocumentTitles().transactionDetailsIdentity),
  });

  if (loading || !data) {
    return <LoadingIndicator />;
  }

  if (data.documentBundle?.__typename !== "DocumentBundle") {
    throw new Error(`Expected DocumentBundle, got ${data.documentBundle?.__typename}`);
  }

  return (
    <IdentityTabV2AnalyticsProvider>
      <IdentityDetails bundle={data.documentBundle} organization={organization} />
    </IdentityTabV2AnalyticsProvider>
  );
}
